/**
 * /admin/accounts — paginated tenant list with filter sidebar + search.
 *
 * Columns: name, slug, status pill, tier, MRR placeholder, customers, last
 * activity, actions (View detail).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { type AdminAccount, adminApi } from '../admin_api';

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  trial: 'bg-blue-100 text-blue-800',
  past_due: 'bg-yellow-100 text-yellow-800',
  grace: 'bg-orange-100 text-orange-800',
  suspended: 'bg-red-100 text-red-800',
  pending_delete: 'bg-red-200 text-red-900',
  deleted: 'bg-gray-100 text-gray-500',
};

const TIER_LABELS: Record<string, string> = {
  starter: 'Starter',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

const STATUS_OPTIONS = [
  '',
  'active',
  'trial',
  'past_due',
  'grace',
  'suspended',
  'pending_delete',
  'deleted',
];
const TIER_OPTIONS = ['', 'starter', 'pro', 'enterprise'];

export function AdminAccounts() {
  const [rows, setRows] = useState<AdminAccount[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [tier, setTier] = useState('');
  const [sort, setSort] = useState('created_at');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  function load() {
    setLoading(true);
    const params: Parameters<typeof adminApi.listAccounts>[0] = { sort, limit, offset };
    if (search) params.search = search;
    if (status) params.status = status;
    if (tier) params.tier = tier;
    adminApi
      .listAccounts(params)
      .then((r) => {
        setRows(r.rows);
        setTotal(r.total);
        setLoading(false);
      })
      .catch((e: Error) => {
        setError(e.message);
        setLoading(false);
      });
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    load();
  }, [search, status, tier, sort, offset]);

  const totalPages = Math.ceil(total / limit);
  const page = Math.floor(offset / limit) + 1;

  return (
    <div className="flex gap-6">
      {/* Filter sidebar */}
      <aside className="w-48 shrink-0 space-y-4">
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
            className="w-full text-sm border border-gray-200 rounded px-2 py-1.5"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s || 'Any'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1">
            Tier
          </label>
          <select
            value={tier}
            onChange={(e) => {
              setTier(e.target.value);
              setOffset(0);
            }}
            className="w-full text-sm border border-gray-200 rounded px-2 py-1.5"
          >
            {TIER_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t ? (TIER_LABELS[t] ?? t) : 'Any'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1">
            Sort
          </label>
          <select
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              setOffset(0);
            }}
            className="w-full text-sm border border-gray-200 rounded px-2 py-1.5"
          >
            <option value="created_at">Newest first</option>
            <option value="updated_at">Recently updated</option>
            <option value="name">Name (A–Z)</option>
          </select>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="text-xl font-semibold">Accounts</h1>
          <span className="text-sm text-muted-foreground num">{total} total</span>
          <div className="ml-auto">
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              placeholder="Search name or slug…"
              className="text-sm border border-gray-200 rounded px-3 py-1.5 w-64"
            />
          </div>
        </div>

        {error && <p className="text-sm text-red-600 mb-4">Error: {error}</p>}

        <div className="bg-white rounded-lg border border-[oklch(88%_0.05_70)] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Name / Slug
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Status
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Tier
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Currency
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Created
                </th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No accounts found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      <p className="font-medium">{row.name}</p>
                      <p className="text-xs text-muted-foreground num">{row.slug}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[row.status] ?? 'bg-gray-100 text-gray-600'}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {TIER_LABELS[row.tier] ?? row.tier}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground num">{row.currency}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs num">
                      {row.createdAt.slice(0, 10)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/accounts/${row.id}`}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              className="px-3 py-1.5 border border-gray-200 rounded disabled:opacity-40"
            >
              ← Previous
            </button>
            <span className="text-muted-foreground num">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
              className="px-3 py-1.5 border border-gray-200 rounded disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
